import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { InviteRepresentativeRequestDTO } from 'src/app/shared/models/dtos/requests/invite-representative-request-dto/invite-representative-request-dto';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { BusinessUpdateRequestDTO } from 'src/app/shared/models/dtos/requests/business-update-request-dto/business-update-request-dto';
import { AdminRegisterDTO } from 'src/app/shared/models/dtos/responses/admin-register/admin-register-dto';
import { AdministratorDTO } from 'src/app/shared/models/dtos/responses/administrator-dto/administrator-dto';
import { BusinessCard } from 'src/app/shared/models/dtos/responses/business-card/business-card';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { Country } from 'src/app/shared/models/dtos/responses/country/country';
import { InviteRepresentativeResponseDTO } from 'src/app/shared/models/dtos/responses/invite-representative-response-dto/invite-representative-response-dto';
import { GenericResponseDTO } from 'src/app/shared/models/dtos/responses/message-response-dto/message-response-dto';
import { ComplianceUrlsResponseDTO } from 'src/app/shared/models/dtos/responses/compliance-urls-response-dto';
import { RegionsCount } from 'src/app/shared/models/dtos/responses/regions-count/regions-count';
import { VipSeasonsDTO } from 'src/app/shared/models/dtos/responses/vip-seasons-dto/vip-seasons-dto';
import { VipRequestDTO } from 'src/app/shared/models/dtos/requests/vip-config-request-dto/vip-config-request-dto';
import { DownloadReportDTO } from 'src/app/shared/models/dtos/download-report/download-report-dto';
import { DownloadReportResponseDTO } from 'src/app/shared/models/dtos/download-report/download-report-response-dto';
import { DetailedRegion } from 'src/app/shared/models/dtos/region/detailed-region';
import { VipConfigSeriesDTO } from 'src/app/shared/models/dtos/responses/vip-config-series-dto/vip-config-series-dto';
import { VipConfigSingleRequestDTO } from '../../../shared/models/dtos/requests/vip-config-request-dto/vip-config-single-request-dto';

@Injectable({ providedIn: 'root' })
/**
 * Service to interact with LnR's User Management Service
 */
export class UserService {
  private backendAuthURL = '/ums/api/a';
  private backendURL = '/ums/api';

  /**
   * Injects HttpClient to interact with LnR Backend
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * Retrieves admin info after okta login
   * @return {Observable<AdminRegisterDTO>}
   */
  retrieveAdminLoginInfo(): Observable<AdminRegisterDTO> {
    const endpoint = 'sysadmin/account';
    return this.httpClient.post<AdminRegisterDTO>(
      this.buildAuthURL(endpoint),
      null
    );
  }

  /**
   * Retrieves admin info
   * @return {Observable<AdministratorDTO>}
   */
  getAdminInfo(): Observable<AdministratorDTO> {
    const endpoint = 'sysadmin/info';
    return this.httpClient.get<AdministratorDTO>(this.buildAuthURL(endpoint));
  }

  /**
   * @return {Observable<Region[]>} a list of available regions
   */
  getRegions(): Observable<Region[]> {
    const endpoint = 'region';
    return this.httpClient.get<Region[]>(this.buildURL(endpoint));
  }

  /**
   * @return {Observable<Region[]>} a list of available regions
   */
  getCountries(): Observable<Country[]> {
    const endpoint = 'country';
    return this.httpClient.get<Country[]>(this.buildURL(endpoint));
  }

  /**
   * @return {Observable<Region[]>} a list of available regions
   */
  getCountriesByAdmin(): Observable<Country[]> {
    const endpoint = 'country/admin';
    return this.httpClient.get<Country[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Returns a list of countries
   * @return {Observable<DetailedRegion[]>}
   */
  listCountries(): Observable<DetailedRegion[]> {
    const endpoint = 'region/detailed';
    return this.httpClient.get<DetailedRegion[]>(this.buildURL(endpoint));
  }

  /**
   * Returns a list of business unification requests
   * @param {Object} queryParams
   * @return {Observable<any[]>}
   */
  getBusinessUnificationRequests(queryParams: {
    status?: any;
  }): Observable<any[]> {
    const endpoint = 'business-unification';
    const params: any = queryParams as any;
    return this.httpClient.get<any[]>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * Returns a business unification request details by id
   * @param {number} id
   * @return {Observable<any[]>}
   */
  getBusinessUnificationDetails(id: number): Observable<any[]> {
    const endpoint = `business-unification/${id}`;
    return this.httpClient.get<any[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Creates a new Business Unification Request
   * @param {any} data
   * @return {Observable<any[]>}
   */
  createNewBusinessUnificationRequest(data: any): Observable<any[]> {
    const endpoint = 'business-unification';
    return this.httpClient.post<any[]>(this.buildAuthURL(endpoint), data);
  }

  /**
   * Accept a business unification request in 'pending' status
   * @param {number} id
   * @return {Observable<any>}
   */
  acceptBusinessUnificationRequest(id: number): Observable<any> {
    const endpoint = `business-unification/accept/${id}`;
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), {});
  }

  /**
   * Reject a business unification request in 'pending' status
   * @param {number} id
   * @return {Observable<any>}
   */
  rejectBusinessUnificationRequest(id: number): Observable<any> {
    const endpoint = `business-unification/reject/${id}`;
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), {});
  }

  /**
   * Updates sys admin's language
   * @param {string} language
   * @return {Observable<AdministratorDTO>}
   */
  changeLanguage(language: 'en' | 'pt' | 'es'): Observable<AdministratorDTO> {
    const endpoint = 'sysadmin/language';
    return this.httpClient.put<AdministratorDTO>(this.buildAuthURL(endpoint), {
      language,
    });
  }

  /**
   * Updates sys admin's profile picture
   * @param {string} picture
   * @return {Observable<AdministratorDTO>}
   */
  updateAdminPicture(picture: string): Observable<AdministratorDTO> {
    const endpoint = 'sysadmin/picture';
    return this.httpClient.put<AdministratorDTO>(this.buildAuthURL(endpoint), {
      picture,
    });
  }

  /**
   * Updates sys admin's language
   * @param {number} regionId
   * @param {string} language
   * @param {string} name
   * @param {string} picture
   * @return {Observable<AdministratorDTO>}
   */
  updateOnboardingData(
    regionId: number,
    language: 'en' | 'pt' | 'es',
    name: string,
    picture?: string
  ): Observable<AdministratorDTO> {
    const endpoint = 'sysadmin/onboard';
    return this.httpClient.put<AdministratorDTO>(this.buildAuthURL(endpoint), {
      regionId,
      language,
      name,
      picture,
    });
  }

  /**
   * Get current active system settings
   * @return {Observable<any>} all system settings
   */
  getSystemSettings(): Observable<any> {
    const endpoint = 'system_settings';
    return this.httpClient.get<any>(this.buildAuthURL(endpoint));
  }

  /**
   * Get system settings audit
   * @param {Object} queryParams
   * @return {Observable<any>} all audit system settings
   */
  getSystemSettingsHistory(queryParams: {
    page: number;
    pageSize: number;
  }): Observable<any> {
    const endpoint = 'system_settings/history';
    const params: any = queryParams as any;
    return this.httpClient.get<any>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * Create a new schedule update system settings
   * @param {any} data
   * @return {Observable<any>} all system settings
   */
  updateSystemSettings(data: any): Observable<any> {
    const endpoint = 'system_settings';
    return this.httpClient.put<any>(this.buildAuthURL(endpoint), data);
  }

  /**
   * Update a scheduled system settings
   * @param {number} id
   * @param {any} data
   * @return {Observable<any>} all system settings
   */
  updateScheduledSystemSettings(id: number, data: any): Observable<any> {
    const endpoint = `system_settings/${id}`;
    return this.httpClient.put<any>(this.buildAuthURL(endpoint), data);
  }

  /**
   * Cancel a scheduled system settings
   * @param {number} id
   * @return {Observable<any>} all system settings
   */
  cancelScheduledSystemSettings(id: number): Observable<any> {
    const endpoint = `system_settings/${id}`;
    return this.httpClient.delete<any>(this.buildAuthURL(endpoint));
  }

  /**
   * Get business list from admin region
   * @param {Object} queryParams
   * @return {Observable<BusinessCard[]>}
   */
  getBusinesses(queryParams: {
    page: number;
    pageSize: number;
    vip?: boolean;
    orderAsc?: boolean;
    search?: string;
    regionId?: number;
  }) {
    const endpoint = 'business/region';
    const params: any = queryParams as any;
    return this.httpClient.get<BusinessCard[]>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * Get active business list from an specific country
   * @param {number} countryId
   * @return {Observable<BusinessCard[]>}
   */
  getBusinessesByCountry(countryId: number) {
    const endpoint = `business/country/${countryId}`;
    return this.httpClient.get<BusinessCard[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Get business summary
   * @param {number} id
   * @return {Observable<BusinessSummaryResponseDTO>}
   */
  getBusinessSummary(id: number): Observable<BusinessSummaryResponseDTO> {
    const endpoint = `business/summary/${id}`;
    return this.httpClient.get<BusinessSummaryResponseDTO>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * Get all business levels grouped by family
   * @return {Observable<BusinessSummaryResponseDTO>}
   */
  getAllBusinessLevelsGroupedByFamily(): Observable<any> {
    const endpoint = 'business_level/all/by_family/';
    return this.httpClient.get<any>(this.buildAuthURL(endpoint));
  }

  /**
   * Update business level
   * @param {number} businessId
   * @param {{}} dto
   * @return {Observable<any>}
   */
  updateBusinessLevel(
    businessId: number,
    dto: { businessLevel: number; resellerName: string | null; season: number }
  ) {
    const endpoint = `business/business_vip/${businessId}`;
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), dto);
  }

  /**
   * Get business types
   * @return {Observable<string[]>} a list of available business types
   */
  getBusinessTypes(): Observable<string[]> {
    const endpoint = 'business/types';
    return this.httpClient.get<string[]>(this.buildURL(endpoint));
  }

  /**
   * Update business data
   * @param {number} businessId
   * @param {BusinessUpdateRequestDTO} business
   * @return {Observable<BusinessUpdateRequestDTO>}
   */
  updateBusiness(businessId: number, business: BusinessUpdateRequestDTO) {
    const endpoint = `business/update/${businessId}`;

    return this.httpClient.put<BusinessUpdateRequestDTO>(
      this.buildAuthURL(endpoint),
      business
    );
  }

  /**
   * Update admin region array
   * @param {number} sysadminId
   * @param {number[]} regionIds
   * @return {Observable<AdministratorDTO>}
   */
  updateAdminRegions(
    sysadminId: number,
    regionIds: number[]
  ): Observable<AdministratorDTO> {
    const endpoint = 'sysadmin/region';

    return this.httpClient.put<AdministratorDTO>(this.buildAuthURL(endpoint), {
      sysadminId,
      regionIds,
    });
  }

  /**
   * Update admin role
   * @param {number} sysadminId
   * @param {string} role
   * @param {number[]} regionIds
   * @return {Observable<AdministratorDTO>}
   */
  updateAdminRole(
    sysadminId: number,
    role: string,
    regionIds: number[]
  ): Observable<AdministratorDTO> {
    const endpoint = 'sysadmin/role';

    return this.httpClient.put<AdministratorDTO>(this.buildAuthURL(endpoint), {
      sysadminId,
      role,
      regionIds,
    });
  }

  /**
   * Delete physically sysAdmin user
   * @param {number} sysadminId
   * @return {GenericResponseDTO}
   */
  deleteAdmin(sysadminId: number): Observable<GenericResponseDTO> {
    const endpoint = `sysadmin/${sysadminId}`;
    return this.httpClient.delete<GenericResponseDTO>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * @param {string} email
   * @return {GenericResponseDTO}
   */
  deleteInvite(email: string): Observable<GenericResponseDTO> {
    const endpoint = `invite/email/${email}`;
    return this.httpClient.delete<GenericResponseDTO>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * @param {number} representativeId
   * @return {Observable<number>}
   */
  promoteRepresentative(representativeId: number): Observable<number> {
    const endpoint = `sysadmin/representative/promote/${representativeId}`;
    return this.httpClient
      .get<GenericResponseDTO>(this.buildAuthURL(endpoint))
      .pipe(map((response) => parseInt(response.message)));
  }

  /**
   * @param {number} representativeId
   * @return {Observable<number>}
   */
  deleteRepresentative(representativeId: number): Observable<number> {
    const endpoint = `sysadmin/representative/disable/${representativeId}`;
    return this.httpClient
      .delete<GenericResponseDTO>(this.buildAuthURL(endpoint))
      .pipe(map((response) => parseInt(response.message)));
  }

  /**
   * @param {number} businessId
   * @return {Observable<Representative[]>}
   */
  listRepresentatives(businessId: number): Observable<Representative[]> {
    const endpoint = `representative/list-from-business/${businessId}`;
    return this.httpClient.get<Representative[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Builds URL from endpoint with authentication
   * @param {string} endpoint
   * @return {string}
   */
  private buildAuthURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }

  /**
   * Builds URL from endpoint
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendURL}/${endpoint}`;
  }

  /**
   * @param {InviteRepresentativeRequestDTO} inviteRepresentativeRequestDTO
   * @return {Observable<string>}
   */
  inviteRepresentative(
    inviteRepresentativeRequestDTO: InviteRepresentativeRequestDTO
  ): Observable<string> {
    const endpoint = 'business/sysadmin/representative/invite';
    return this.httpClient
      .post<GenericResponseDTO>(
        this.buildAuthURL(endpoint),
        inviteRepresentativeRequestDTO
      )
      .pipe(map((response) => response.message));
  }

  /**
   * @param {number} businessId
   * @return {Observable<InviteRepresentativeResponseDTO[]>}
   */
  listInvitees(
    businessId: number
  ): Observable<InviteRepresentativeResponseDTO[]> {
    const endpoint = `invite/list-from-business/${businessId}`;
    return this.httpClient.get<InviteRepresentativeResponseDTO[]>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * @return {Observable<any>}
   */
  downloadMigrationTemplate(): Observable<any> {
    const endpoint = 'sysadmin/download/migration-template';
    return this.httpClient
      .get(this.buildAuthURL(endpoint), {
        responseType: 'blob',
      })
      .pipe(
        map((data) => {
          return {
            data,
            filename: 'template.csv',
          };
        })
      );
  }

  /**
   * @param {number} businessId
   * @return {Observable<[CouponVip]>}
   */
  getCouponVipByBusinessId(businessId: number) {
    const endpoint = `business_vip/business/${businessId}`;
    return this.httpClient.get<any>(this.buildAuthURL(endpoint));
  }

  /**
   * @return {Observable<ComplianceUrlsResponseDTO>}
   */
  getTermsOfUseAndPrivacyConditionsURLs(): Observable<ComplianceUrlsResponseDTO> {
    const endpoint = 'compliance/terms-and-privacy';
    return this.httpClient.get<ComplianceUrlsResponseDTO>(
      this.buildURL(endpoint)
    );
  }

  /**
   * @param {Object} queryParams //the params must be changed!!
   * @return {Observable<any[]>}
   */
  listAdmins(queryParams: {
    page: number;
    pageSize: number;
    orderAsc?: boolean;
    global?: boolean;
    regional?: boolean;
    viewOnly?: boolean;
    regions?: Region[];
    search?: string | null;
  }): Observable<AdministratorDTO[]> {
    const endpoint = 'sysadmin';
    const params: any = queryParams as any;

    return this.httpClient.get<AdministratorDTO[]>(
      this.buildAuthURL(endpoint),
      {
        params,
      }
    );
  }

  /**
   * Regional admin count
   * @return {Observable<any>}
   */
  countRegionalAdmins(): Observable<RegionsCount[]> {
    const endpoint = 'sysadmin/region';

    return this.httpClient.get<RegionsCount[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Get VIP region
   * @param {number} regionId
   * @return {Observable<VipSeasonsDTO>}
   */
  getVipRegion(regionId: number): Observable<VipSeasonsDTO> {
    const endpoint = `vip_config/region/${regionId}`;

    return this.httpClient.get<VipSeasonsDTO>(this.buildAuthURL(endpoint));
  }

  /**
   *
   * @param {VipRequestDTO} param
   * @return {Observable<VipRequestDTO>}
   */
  updateVipRegion(param: VipRequestDTO): Observable<VipRequestDTO> {
    const endpoint = 'vip_config/';

    return this.httpClient.put<VipRequestDTO>(
      this.buildAuthURL(endpoint),
      param
    );
  }

  /**
   * Get VIP config by series
   * @return {Observable<VipSeasonsDTO[]>}
   */
  getVipConfigSeries(): Observable<VipConfigSeriesDTO[]> {
    const endpoint = 'vip_config_series/';

    return this.httpClient.get<VipConfigSeriesDTO[]>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * Update one Vip config by series
   * @param {any} requestBody request body
   * @return {any} confirmation
   */
  updateVipConfigSeries(requestBody: any): any {
    const endpoint = 'vip_config_series/';

    return this.httpClient.put<any>(this.buildAuthURL(endpoint), requestBody);
  }

  /**
   * Gets a user service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }

  /**
   * Download reports
   * @param {DownloadReportDTO} param
   * @return {Observable<string>}
   */
  getReportDownload(param: DownloadReportDTO): Observable<string> {
    const endpoint = 'report';
    return this.httpClient.post(this.buildAuthURL(endpoint), param, {
      responseType: 'text',
    });
  }

  /**
   * Get pre-signed URL to download report
   * @param {string} filename
   * @return {Observable<string>}
   */
  getPresignedReport(filename: string): Observable<string> {
    const endpoint = `report/presigned/${filename}`;
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(this.buildAuthURL(endpoint), {
      headers,
      responseType: 'text',
    });
  }

  /**
   * Get download report status
   * @param {string} uuid
   * @return {Observable<DownloadReportResponseDTO>}
   */
  getDownloadReportStatus(uuid: string): Observable<DownloadReportResponseDTO> {
    const endpoint = `report/status/${uuid}`;

    return this.httpClient.get<DownloadReportResponseDTO>(
      this.buildAuthURL(endpoint)
    );
  }

  /**
   * Delete a business level by id
   * @param {string} businessLevelId
   * @return {Observable<any>}
   */
  markToDeleteBusinessVipLevel(businessLevelId: string): Observable<any> {
    const endpoint = `business_level/marked_for_deletion/${businessLevelId}`;
    return this.httpClient.patch<GenericResponseDTO>(
      this.buildAuthURL(endpoint),
      null
    );
  }

  /**
   * Create a single vip by family Id
   * @param {VipConfigSingleRequestDTO} content
   * @return {Observable<any>}
   */
  createSingleBusinessVipLevel(
    content: VipConfigSingleRequestDTO
  ): Observable<any> {
    const endpoint = 'vip_config/single';
    return this.httpClient.post<any>(this.buildAuthURL(endpoint), content);
  }

  /**
   * Get pre-signed URL to download report
   * @param {number} id
   * @return {Observable<string>}
   */
  getBusinessUnificationReport(id: number): Observable<string> {
    const endpoint = `business-unification/${id}/download-report`;
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(this.buildAuthURL(endpoint), {
      headers,
      responseType: 'text',
    });
  }
}
